import {
  ApiScope,
  apiScopeHint,
  apiScopeLabel,
  PartnerAppRegistration,
  PartnerAppRelease,
} from '@kanos/domain';
import { Button, CheckboxGroupField, StatusBadge, TextField } from '@kanos/ui';
import {
  AddButton,
  Card,
  checkForm,
  fieldRule,
  formatDate,
  FormDialog,
  QueryContent,
  type Me,
} from '@kanos/web-core';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, PlugZap } from 'lucide-react';
import { useState } from 'react';
import {
  partnerAppsQuery,
  useRegisterPartnerApp,
  useReleasePartnerApp,
  useSuspendPartnerApp,
  type PartnerAppAdmin,
} from './queries';

const scopeOptions = ApiScope.options.map((id) => ({
  id,
  label: apiScopeLabel[id],
  description: apiScopeHint[id],
}));

/**
 * The partner apps of Kanos (ADR-015): registered here for testing - firms connect them to their
 * sandbox only - released after the review, suspended when the contract ends: every installation
 * in every firm and sandbox is revoked at once.
 */
export function PartnerAppsPage({ me }: { me: Me }) {
  const apps = useQuery(partnerAppsQuery);
  const [registering, setRegistering] = useState(false);
  const [suspending, setSuspending] = useState<PartnerAppAdmin | null>(null);
  const [releasing, setReleasing] = useState<PartnerAppAdmin | null>(null);

  return (
    <Card
      id="platform-partner-apps"
      title="Partner-Apps"
      description="Apps von Partnern. Registriert sind sie zunächst zum Testen – nur mit Sandboxes verbindbar. Mit echten Kanzleien erst nach Prüfung von Vertrag und AVV und der Freigabe."
      action={<AddButton label="Partner-App registrieren" onPress={() => setRegistering(true)} />}
    >
      <QueryContent query={apps}>
        {(list) =>
          list.length === 0 ? (
            <p className="text-sm text-muted">Noch keine Partner-App registriert.</p>
          ) : (
            <ul className="divide-y divide-hairline border-y border-hairline">
              {list.map((app) => (
                <PartnerAppRow
                  key={app.id}
                  app={app}
                  onRelease={() => setReleasing(app)}
                  onSuspend={() => setSuspending(app)}
                />
              ))}
            </ul>
          )
        }
      </QueryContent>
      {registering && <RegisterDialog onClose={() => setRegistering(false)} />}
      {suspending && <SuspendDialog app={suspending} onClose={() => setSuspending(null)} />}
      {releasing && <ReleaseDialog me={me} app={releasing} onClose={() => setReleasing(null)} />}
    </Card>
  );
}

function StageBadge({ app }: { app: PartnerAppAdmin }) {
  if (app.suspendedAt) return <StatusBadge tone="danger">gesperrt</StatusBadge>;
  if (app.reviewedAt === null) return <StatusBadge tone="warning">Test</StatusBadge>;
  return <StatusBadge tone="success">freigegeben</StatusBadge>;
}

function PartnerAppRow({
  app,
  onRelease,
  onSuspend,
}: {
  app: PartnerAppAdmin;
  onRelease: () => void;
  onSuspend: () => void;
}) {
  return (
    <li className="flex flex-wrap items-start gap-x-4 gap-y-2 py-3.5">
      <PlugZap aria-hidden className="mt-1 size-4 shrink-0 text-muted" />
      <div className="grid min-w-0 flex-1 basis-64 gap-0.5">
        <p className="flex flex-wrap items-center gap-2 font-semibold">
          {app.name}
          <StageBadge app={app} />
        </p>
        <p className="text-sm text-muted">
          {app.vendor} ·{' '}
          <a
            href={app.website}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-1 underline underline-offset-2"
          >
            Website
            <ExternalLink aria-hidden className="size-3.5" />
          </a>
        </p>
        <p className="text-sm text-muted">{app.scopes.map((s) => apiScopeLabel[s]).join(' · ')}</p>
        <p className="text-xs text-muted">
          {app.reviewedAt
            ? `geprüft am ${formatDate(app.reviewedAt)} von ${app.reviewedBy ?? ''} · ${app.dpaReference ?? ''}`
            : `registriert am ${formatDate(app.registeredAt)} · nur mit Sandboxes verbindbar`}
        </p>
        <p className="truncate font-mono text-xs text-muted">{app.id}</p>
        {app.suspendedAt && (
          <p className="text-sm text-muted">
            gesperrt am {formatDate(app.suspendedAt)}: {app.suspendedReason}
          </p>
        )}
      </div>
      {!app.suspendedAt && (
        <div className="ml-8 flex flex-wrap gap-2 sm:ml-0">
          {app.reviewedAt === null && (
            <Button variant="secondary" size="sm" onPress={onRelease}>
              Freigeben<span className="sr-only">: {app.name}</span>
            </Button>
          )}
          <Button variant="quiet" size="sm" onPress={onSuspend}>
            Sperren<span className="sr-only">: {app.name}</span>
          </Button>
        </div>
      )}
    </li>
  );
}

function RegisterDialog({ onClose }: { onClose: () => void }) {
  const register = useRegisterPartnerApp();
  const [name, setName] = useState('');
  const [vendor, setVendor] = useState('');
  const [website, setWebsite] = useState('');
  const [scopes, setScopes] = useState<ApiScope[]>([]);
  const [redirectUris, setRedirectUris] = useState('');
  const [jwksUri, setJwksUri] = useState('');
  const [unfit, setUnfit] = useState(false);
  const shape = PartnerAppRegistration.shape;

  const submit = () => {
    const checked = checkForm(PartnerAppRegistration, {
      name,
      vendor,
      website,
      scopes,
      redirectUris: redirectUris
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean),
      jwksUri,
    });
    setUnfit(checked === null);
    if (checked) register.mutate(checked, { onSuccess: onClose });
  };

  return (
    <FormDialog
      title="Partner-App registrieren"
      description="Zum Testen: Kanzleien können die App danach mit ihrer Sandbox verbinden. Mit echten Kanzleien erst nach der Freigabe."
      submitLabel="Registrieren"
      isPending={register.isPending}
      error={register.error}
      unfit={unfit}
      onSubmit={submit}
      draft={{ name, vendor, website, scopes, redirectUris, jwksUri }}
      onClose={onClose}
    >
      <TextField
        label="Name der App"
        placeholder="z. B. Kanzleisoftware Muster"
        value={name}
        onChange={setName}
        isRequired
        validate={fieldRule(shape.name, 'Bitte geben Sie den Namen an.')}
      />
      <TextField
        label="Anbieter"
        placeholder="z. B. Muster Software GmbH"
        value={vendor}
        onChange={setVendor}
        isRequired
        validate={fieldRule(shape.vendor, 'Bitte geben Sie den Anbieter an.')}
      />
      <TextField
        label="Website"
        placeholder="z. B. https://kanzleisoftware.example"
        value={website}
        onChange={setWebsite}
        isRequired
        validate={fieldRule(shape.website, 'Bitte eine https-Adresse.')}
      />
      <CheckboxGroupField
        label="Erlaubte Berechtigungen"
        options={scopeOptions}
        value={scopes}
        onChange={setScopes}
        isRequired
        errorMessage="Bitte wählen Sie mindestens eine Berechtigung."
      />
      <TextField
        label="Rücksprungadressen"
        description="Eine Adresse je Zeile; https, für Entwicklungswerkzeuge auch die Loopback-Adresse."
        placeholder="z. B. https://kanzleisoftware.example/kanos/callback"
        value={redirectUris}
        onChange={setRedirectUris}
        multiline
        isRequired
      />
      <TextField
        label="Adresse der öffentlichen Schlüssel (JWKS)"
        placeholder="z. B. https://kanzleisoftware.example/.well-known/jwks.json"
        value={jwksUri}
        onChange={setJwksUri}
        isRequired
        validate={fieldRule(shape.jwksUri, 'Bitte eine https-Adresse.')}
      />
    </FormDialog>
  );
}

function ReleaseDialog({
  me,
  app,
  onClose,
}: {
  me: Me;
  app: PartnerAppAdmin;
  onClose: () => void;
}) {
  const release = useReleasePartnerApp();
  const [dpaReference, setDpaReference] = useState('');
  const [reviewedBy, setReviewedBy] = useState(me.displayName ?? '');
  // Optional: firms see it on the consent page and confirm the agreement either way (#40).
  const [dpaUrl, setDpaUrl] = useState('');
  const [unfit, setUnfit] = useState(false);
  const shape = PartnerAppRelease.shape;
  const link = dpaUrl.trim() === '' ? undefined : dpaUrl.trim();

  const submit = () => {
    const checked = checkForm(PartnerAppRelease, {
      dpaReference,
      reviewedBy,
      ...(link === undefined ? {} : { dpaUrl: link }),
    });
    setUnfit(checked === null);
    if (checked) release.mutate({ id: app.id, ...checked }, { onSuccess: onClose });
  };

  return (
    <FormDialog
      title={`„${app.name}“ freigeben?`}
      description="Nur nach abgeschlossener Prüfung: Vertrag und Auftragsverarbeitungsvertrag liegen vor. Danach können Kanzleien die App auch mit ihren echten Daten verbinden."
      submitLabel="Freigeben"
      isPending={release.isPending}
      error={release.error}
      unfit={unfit}
      onSubmit={submit}
      draft={{ dpaReference, reviewedBy, dpaUrl }}
      onClose={onClose}
    >
      <TextField
        label="AVV-Bezug"
        placeholder="z. B. AVV-2026-017"
        value={dpaReference}
        onChange={setDpaReference}
        isRequired
        validate={fieldRule(shape.dpaReference, 'Bitte geben Sie den AVV-Bezug an.')}
      />
      <TextField
        label="Link zum AVV"
        placeholder="z. B. https://anbieter.example/avv"
        description="Kanzleien sehen ihn, wenn sie die App verbinden. Optional."
        type="url"
        value={dpaUrl}
        onChange={setDpaUrl}
        validate={(value) =>
          fieldRule(
            shape.dpaUrl,
            'Bitte eine https-Adresse angeben.',
          )(value.trim() === '' ? undefined : value.trim())
        }
      />
      <TextField
        label="Geprüft von"
        placeholder="z. B. Paula Plattform"
        value={reviewedBy}
        onChange={setReviewedBy}
        isRequired
        validate={fieldRule(shape.reviewedBy, 'Bitte geben Sie an, wer geprüft hat.')}
      />
    </FormDialog>
  );
}

function SuspendDialog({ app, onClose }: { app: PartnerAppAdmin; onClose: () => void }) {
  const suspend = useSuspendPartnerApp();
  const [reason, setReason] = useState('');
  const [unfit, setUnfit] = useState(false);

  const submit = () => {
    const fits = reason.trim().length >= 3;
    setUnfit(!fits);
    if (fits) suspend.mutate({ id: app.id, reason: reason.trim() }, { onSuccess: onClose });
  };

  return (
    <FormDialog
      title={`„${app.name}“ sperren?`}
      description="Keine Kanzlei kann die App danach verbinden, und jede bestehende Verbindung endet sofort. Das lässt sich nicht rückgängig machen."
      submitLabel="Sperren"
      isPending={suspend.isPending}
      error={suspend.error}
      unfit={unfit}
      onSubmit={submit}
      draft={reason}
      onClose={onClose}
    >
      <TextField
        label="Grund"
        placeholder="z. B. AVV zum 31.12. gekündigt"
        value={reason}
        onChange={setReason}
        multiline
        isRequired
      />
    </FormDialog>
  );
}
